import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonContent, Platform, PopoverController } from '@ionic/angular';
import { ApiService } from 'src/app/services/api.service';
import { PopoverSocialShareComponent } from 'src/app/components/popover-social-share/popover-social-share.component';
import { SeoService } from 'src/app/services/seo.service';
import { SEOSchema } from 'src/app/types/seo';
import { environment } from 'src/environments/environment';
import { ImageInterface } from 'src/app/types/image';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { PostItem } from 'src/app/types/post';

@Component({
  selector: 'app-offers-detail',
  templateUrl: './offers-detail.page.html',
  styleUrls: ['./offers-detail.page.scss'],
})
export class OffersDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  desktop: boolean = false;
  tablet: boolean = false;
  xhrInProgress: boolean = false;
  currentDayName: string;
  featuredImage: ImageInterface;
  websiteUrl: string;
  ctaUrl: string;
  startDate: string | number;
  endDate: string | number;

  data: PostItem;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'Offer',
  };

  slideOpts = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 1.2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2.4,
        spaceBetween: 40,
      },
      1024: {
        slidesPerView: 3.33,
        spaceBetween: 40,
      },
    },
  };

  constructor(
    private platform: Platform,
    private seo: SeoService,
    private API: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    public popoverController: PopoverController,
    private jsonLdService: JsonLdService
  ) {
    this.currentDayName = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();

    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
      this.tablet = this.platform.is('tablet');
    });

    this.platform.resize.subscribe(async () => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
    });
  }

  /**
   * Link to offer detail
   */
  linkToDetail(item) {
    this.router.navigateByUrl(`/offers/${item.slug}`);
  }

  /**
   * Present social share popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverSocialShareComponent,
      event: ev,
      translucent: true,
      cssClass: 'social-popover',
    });
    return await popover.present();
  }

  ngOnInit() {
    this.route.data.subscribe((response: any) => {
      if (typeof response.data.offer.category === 'string') {
        response.data.offer.category = JSON.parse(response.category);
      }

      if (response.data.offer.featuredImage) {
        this.featuredImage = {
          id: response.data.offer.featuredImageId,
          ratio: response.data.offer.featuredImageSelectedRatio,
          url: response.data.offer.featuredImage,
          stretched: false,
          withBackground: false,
        };
      }

      this.data = response.data;

      // Cast dates
      if (
        this.data.offer?.typeSettings?.startDate &&
        this.data.offer?.typeSettings?.endDate
      ) {
        this.startDate = Date.parse(
          this.data.offer?.typeSettings.startDate.replace(/ /g, 'T')
        );
        this.endDate = Date.parse(
          this.data.offer?.typeSettings.endDate.replace(/ /g, 'T')
        );
      }

      if (this.data.offer?.typeSettings?.ctaUrl) {
        if (this.data.offer.typeSettings.ctaUrl.indexOf('://') < 0) {
          this.ctaUrl = '//' + this.data.offer.typeSettings.ctaUrl;
        } else {
          this.ctaUrl = this.data.offer.typeSettings.ctaUrl;
        }
      }

      if (this.data.offer?.offerContext?.typeGeneral?.website) {
        if (
          this.data.offer.offerContext.typeGeneral.website.indexOf('://') < 0
        ) {
          this.websiteUrl =
            '//' + this.data.offer.offerContext.typeGeneral.website;
        } else {
          this.websiteUrl = this.data.offer.offerContext.typeGeneral.website;
        }
      }

      // Set Meta & Social tags
      if (!this.cordova) {
        let schemaData = { ...this.schema };

        if (this.data.offer.featuredImage) {
          schemaData = {
            ...schemaData,
            image: this.data.offer.featuredImage,
          };
        }

        // Title tag
        if (this.data.offer.seo?.title) {
          this.seo.setTitle(this.data.offer.seo.title);
          schemaData = {
            ...schemaData,
            name: this.data.offer.seo.title,
          };
        } else {
          this.seo.setTitle(this.data.offer.title);
          schemaData = {
            ...schemaData,
            name: this.data.offer.title,
          };
        }

        // Meta tags
        if (this.data.offer.seo) {
          this.seo.setMeta(this.data.offer.seo);
          this.seo.setRobotsMeta(this.data.offer.seo);

          schemaData = {
            ...schemaData,
            description: this.data.offer.seo.description,
          };
        }

        // Social tags
        if (this.data.offer.social) {
          if (
            !this.data.offer.social.socialImage &&
            this.data.offer.socialImage
          )
            this.data.offer.social.socialImage = this.data.offer.socialImage;

          this.seo.setSEOMeta(this.data.offer.social);
        }

        // Author
        if (this.data.offer.createdBy) {
          this.seo.setAuthor(this.data.offer.createdBy);
        }

        // Canonical URL
        if (this.data.offer.seo?.canonical_url) {
          this.seo.setCanonicalURL(this.data.offer.seo);
        }

        this.jsonLdService.insertSchema(schemaData);
      }

      this.xhrInProgress = false;
    });
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }
}
